import axios from "axios";
import { camelizeKeys, decamelizeKeys } from "humps";
import {
  PaymentMethod,
  type CheckoutSession,
  type CheckoutSessionConfiguration,
  type Currency,
  type CkoHubVersion,
  type CardData,
} from "@/types";
import { CksCheckoutStateSchema } from "@/services/cosmos/schema/checkout-service";
import COUNTRY_DATA from "@/country-data";
import { encodeCurrency } from "@/lib/codec";
import { type AxiosInstance } from "axios";
import { logInfo, logWarn, logError } from "@/services/logging/logger";
import { getDeviceIdCookie, UNDEFINED_CHECKOUT_TOKEN } from "@/services/cookie";

const humpsIgnoredKeys =
  /(PAY_BY_INSTALMENTS|PAY_NOW|PAY_NEXT_MONTH|PAY_BY_LATER|PAY_BY_FINANCE)/;

function createHttpClient(
  baseURL: string,
  authToken: string,
  options: {
    decamelizeKeys?: boolean;
  } = {
    decamelizeKeys: true,
  },
) {
  const httpClient = axios.create({
    baseURL,
    timeout: 60000,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });

  if (options.decamelizeKeys) {
    httpClient.interceptors.request.use(function (config) {
      return {
        ...config,
        data: decamelizeKeys(config.data),
      };
    });
  }

  httpClient.interceptors.response.use(
    function (response) {
      return {
        ...response,
        data: camelizeKeys(response.data, function (key, convert) {
          return humpsIgnoredKeys.test(key) ? key : convert(key);
        }),
      };
    },
    function (error) {
      // Handle axios errors consistently
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const statusText = error.response.statusText;
        const url = error.config?.url || "unknown";
        const method = error.config?.method?.toUpperCase() || "unknown";

        // Log error with context
        logError(
          "cosmos-client",
          `API request failed: ${method} ${url} - ${status} ${statusText}`,
          error instanceof Error ? error : new Error(String(error)),
          {
            status,
            statusText,
            url,
            method,
            endpoint: url,
          },
        );
      } else if (error.request) {
        // Request was made but no response received (network error, timeout, etc.)
        const url = error.config?.url || "unknown";
        const method = error.config?.method?.toUpperCase() || "unknown";

        logError(
          "cosmos-client",
          `API request failed (no response): ${method} ${url}`,
          error instanceof Error ? error : new Error(String(error)),
          {
            url,
            method,
            endpoint: url,
            errorType: "network_error",
          },
        );
      } else {
        // Something else happened
        logError(
          "cosmos-client",
          "API request setup failed",
          error instanceof Error ? error : new Error(String(error)),
        );
      }

      // Re-throw to allow caller to handle
      return Promise.reject(error);
    },
  );

  return httpClient;
}

export interface CosmosDeviceFingerprint {
  seonEnabled: boolean;
  seonSession: string;
}
export interface CosmosClientOptions {
  checkoutId: string;
  authToken: string;
  deviceFingerprint: CosmosDeviceFingerprint;
}

export const createCosmosClient = ({
  checkoutId,
  authToken,
  deviceFingerprint,
}: CosmosClientOptions) => {
  if (authToken === UNDEFINED_CHECKOUT_TOKEN) {
    logWarn(
      "cosmos-client",
      "Authentication token is unknown (using placeholder token)",
      { checkoutId },
    );
  }
  const httpClient = createHttpClient(
    import.meta.env.VITE_COSMOS_BASE_URL,
    authToken,
  );
  const httpClientPayApi = createHttpClient(
    import.meta.env.VITE_PAY_BASE_URL,
    authToken,
  );
  const httpClientApplePay = createHttpClient(
    import.meta.env.VITE_COSMOS_BASE_URL,
    authToken,
    { decamelizeKeys: false },
  );

  return {
    config() {
      return {
        checkoutId,
        authToken,
        deviceFingerprint,
      };
    },

    getSavedCards: async ({
      merchantId,
      orderId,
    }: {
      merchantId: string;
      orderId?: string | null;
    }) => {
      const params: Record<string, string> = {
        "pm-exls": "CheckoutApplePay",
        merchantId,
        page: "checkout",
      };

      // Only add orderId if it's a non-empty string
      if (orderId && typeof orderId === "string" && orderId.trim() !== "") {
        params.orderId = orderId;
      }

      return await httpClient
        .get("customers/payment/v2/cards", { params })
        .then((response) => response.data);
    },

    tokenizeCard: async (
      customerId: string,
      { number, expiryMonth, expiryYear, cvv }: CardData,
    ): Promise<TokenizeCardResult> => {
      const responseData = (
        await httpClientPayApi.post(
          "tokens",
          {
            type: "card",
            number,
            expiry_month: expiryMonth,
            expiry_year: expiryYear,
            cvv,
          },
          {
            headers: {
              "x-merchant-pk": import.meta.env.VITE_TAMARA_MECHANT_PK,
              "x-customer-id": customerId,
            },
          },
        )
      ).data;

      return {
        tokenType: responseData?.type ?? "",
        token: responseData?.token ?? "",
        fingerprint: responseData?.fingerprint,
        bin: responseData?.bin,
        last4: responseData?.last4,
        expiry_month: responseData?.expiry_month ?? responseData?.expiryMonth,
        expiry_year: responseData?.expiry_year ?? responseData?.expiryYear,
      };
    },

    validateAppleSession: async (
      _config: CheckoutSessionConfiguration,
      validationUrl: string,
      currency: Currency,
      ckoHubVersion: CkoHubVersion,
      countryCode: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): Promise<any> => {
      const responseData = (
        await httpClientApplePay.post(
          "repayment/v2/applepay/validate",
          {
            validationUrl,
            domain:
              window?.location?.host ||
              "",
          },
          {
            headers: {
              ...ckoPublicKeyHeader(currency, ckoHubVersion),
              "x-component": "checkout",
              "x-locale": "en_US",
              "x-country": countryCode,
            },
          },
        )
      ).data;

      logInfo("cosmos-client", "Got Apple Session Validation Result", {
        validationUrl,
        currency,
        countryCode,
        ckoHubVersion,
      });

      return responseData;
    },

    ///////////////////////
    // ABOVE is JERK
    // BELOW is COSMOS
    ///////////////////////

    getCheckoutSessionV3: async () =>
      await httpClient
        .get(`checkout/v3/${checkoutId}`)
        .then((response) => response.data),

    /**
     * Checkout Service API for selecting the payment option.
     * @param paymentOptionOfferId - The payment option offer ID to select.
     * @param tenure - The tenure of the payment option.
     * @returns The selected payment option.
     */
    selectPaymentOption: async (
      paymentOptionOfferId: string,
      tenure?: number,
    ) => {
      return await httpClient
        .post(`checkout/v3/${checkoutId}/select-payment-option`, {
          paymentOptionId: paymentOptionOfferId,
          tenure: tenure,
          termsAccepted: true,
        })
        .then((response) => response.data);
    },

    /**
     * Checkout Service API for selecting the payment method.
     * @param paymentMethod - The payment method to select.
     * @returns The selected payment method.
     */
    selectPaymentMethod: async (
      paymentMethod: PaymentMethod,
      {
        token,
        cardId,
        cvv,
        termsAccepted,
        documentsSigned,
        shouldUseWalletBalance,
        fingerprint,
        cardBin,
        cardLast4,
        cardExpiryMonth,
        cardExpiryYear,
      }: {
        token?: string | null;
        cardId?: string | number;
        cvv?: string;
        termsAccepted?: boolean;
        documentsSigned?: boolean;
        shouldUseWalletBalance?: boolean;
        fingerprint?: string;
        cardBin?: string;
        cardLast4?: string;
        cardExpiryMonth?: string;
        cardExpiryYear?: string;
      },
    ): Promise<CheckoutSession> => {
      const options: PaymentRequestOptions = {
        termsAccepted,
        documentsSigned,
        shouldUseWalletBalance,
      };

      try {
        const newSession =
          paymentMethod === PaymentMethod.APPLE_PAY
            ? (
                await postPaymentApple(
                  deviceFingerprint,
                  httpClientApplePay,
                  checkoutId,
                  token,
                  options,
                )
              ).data
            : (
                await postPayment(
                  deviceFingerprint,
                  paymentMethod,
                  httpClient,
                  checkoutId,
                  token,
                  cardId,
                  cvv,
                  options,
                  fingerprint,
                  cardBin,
                  cardLast4,
                  cardExpiryMonth,
                  cardExpiryYear,
                )
              ).data;

        return CksCheckoutStateSchema.parse(newSession);
      } catch (err) {
        const result = CksCheckoutStateSchema.safeParse(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          camelizeKeys((err as any)?.response?.data ?? {}),
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (result.success) (err as any).session = result.data;

        throw err;
      }
    },

    retryIvr: async (checkoutId: string) => {
      return CksCheckoutStateSchema.parse(
        (await httpClient.post(`checkout/v3/${checkoutId}/retry-ivr`)).data,
      );
    },

    /**
     * Checkout Service API for getting a document.
     * @param documentId - The document ID to get.
     * @returns The document.
     */
    getDocument: async (documentId: string) => {
      return await httpClient
        .get(`checkout/v3/${checkoutId}/documents/${documentId}`)
        .then((response) => response.data);
    },

    /**
     * Checkout Service API for signing the document.
     * @param documentId - The document ID to sign.
     * @returns The signed document.
     */
    signDocument: async (documentId: string) => {
      return await httpClient
        .post(`checkout/v3/${checkoutId}/sign-document`, {
          checkoutId,
          data: { documentId },
        })
        .then((response) => response.data);
    },

    /**
     * Checkout Service API for subscribing to the checkout session.
     * @returns The subscribed checkout session.
     */
    subscribe: async () => {
      return await httpClient
        .post(`checkout/v3/${checkoutId}/subscribe`, {
          checkoutId,
        })
        .then((response) => response.data);
    },

    /**
     * Checkout Service API for unsubscribing from the checkout session.
     * @returns The unsubscribed checkout session.
     */
    unsubscribe: async () => {
      return await httpClient
        .post(`checkout/v3/${checkoutId}/unsubscribe`, {
          checkoutId,
        })
        .then((response) => response.data);
    },

    /**
     * Checkout Service API for going back from payment method selection.
     * @returns The latest checkout session.
     */
    backFromPaymentMethodSelection: async (): Promise<CheckoutSession> => {
      const newSession = (
        await httpClient.post(
          `checkout/v3/${checkoutId}/await_payment_method_selection/back`,
        )
      ).data;

      return CksCheckoutStateSchema.parse(newSession);
    },

    /**
     * Acknowledge early-repayment payment completion.
     * Called after the BE transitions to EARLY_REPAYMENT_COMPLETED so the
     * checkout can resume the normal flow (credit evaluation → plan selection).
     * @returns The updated checkout session.
     */
    acknowledgePayment: async (): Promise<CheckoutSession> => {
      const response = await httpClient.post(
        `checkout/v3/${checkoutId}/acknowledge-payment`,
      );
      return CksCheckoutStateSchema.parse(response.data);
    },

    /**
     * Verify completion of an external step (KYC, questionnaire, etc.).
     */
    verifyExternalStep: async (
      endpoint: string,
    ): Promise<CheckoutSession> => {
      logInfo("cosmos-client", "Verifying external step completion", {
        checkoutId,
        endpoint: `checkout/v3/${checkoutId}/${endpoint}`,
      });

      const response = await httpClient.post(
        `checkout/v3/${checkoutId}/${endpoint}`,
      );

      const session = CksCheckoutStateSchema.parse(response.data);

      logInfo("cosmos-client", "External step verified", {
        checkoutId,
        endpoint,
        sessionId: session.id,
        status: session.status,
      });

      return session;
    },

    /**
     * Called when the user returns from the financing questionnaire UI.
     * @deprecated Prefer verifyExternalStep("verify-questionnaire").
     */
    verifyQuestionnaire: async (): Promise<CheckoutSession> => {
      const response = await httpClient.post(
        `checkout/v3/${checkoutId}/verify-questionnaire`,
      );
      const session = CksCheckoutStateSchema.parse(response.data);
      logInfo("cosmos-client", "Questionnaire verified", {
        checkoutId,
        sessionId: session.id,
        status: session.status,
      });
      return session;
    },

    /**
     * Checkout Service API for reporting 3DS failure.
     * @returns The updated checkout session with the new status.
     */
    report3dsFailure: async (): Promise<CheckoutSession> => {
      logInfo("cosmos-client", "Reporting 3DS failure to backend", {
        checkoutId,
        endpoint: `checkout/v3/${checkoutId}/report-3ds-failure`,
      });

      const response = await httpClient.post(
        `checkout/v3/${checkoutId}/report-3ds-failure`,
      );

      const session = CksCheckoutStateSchema.parse(response.data);

      logInfo("cosmos-client", "3DS failure reported successfully", {
        checkoutId,
        sessionId: session.id,
        status: session.status,
      });

      return session;
    },
  };
};

export type CosmosClient = ReturnType<typeof createCosmosClient>;

export type TokenizeCardResult = {
  token: string;
  tokenType: string;
  fingerprint?: string;
  /** From tokenization response, pass to select-payment-method as-is */
  bin?: string;
  last4?: string;
  expiry_month?: number;
  expiry_year?: number;
};

const ckoPublicKeyHeader = (
  currency: Currency,
  ckoHubVersion: CkoHubVersion,
): Record<string, string> => {
  const countryData = COUNTRY_DATA.find(
    (countryData) => countryData.currency === currency,
  );

  if (!countryData) {
    logWarn(
      "cosmos-client",
      `Failed to find country data to build header for CKO Public Key: ${encodeCurrency(currency)}`,
      { currency, ckoHubVersion },
    );

    return {};
  }

  return {
    "x-checkoutcom-pk":
      ckoHubVersion === "v2"
        ? countryData.ckoPublicKeyV2
        : countryData.ckoPublicKey,
  };
};

export type PaymentRequestOptions = {
  termsAccepted?: boolean;
  documentsSigned?: boolean;
  shouldUseWalletBalance?: boolean;
};

const postPayment = (
  deviceFingerprint: CosmosDeviceFingerprint,
  paymentMethod: PaymentMethod,
  httpClient: AxiosInstance,
  checkoutId: string,
  token?: string | null,
  cardId?: string | number,
  cvv?: string,
  options?: PaymentRequestOptions,
  fingerprint?: string,
  cardBin?: string,
  cardLast4?: string,
  cardExpiryMonth?: string,
  cardExpiryYear?: string,
) => {
  const paymentMethodString =
    new Map<PaymentMethod, string>([
      [PaymentMethod.KNET_CARD, "KNET_CARD"],
      [PaymentMethod.APPLE_PAY, "APPLE_PAY"],
      [PaymentMethod.CREDIT_CARD, "CREDIT_CARD"],
      [PaymentMethod.MADA_CARD, "MADA_CARD"],
      [PaymentMethod.TAMARA_PAY_CARD, "TAMARA_PAY_CARD"],
    ]).get(paymentMethod) ?? "UNKNOWN";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const paymentDetails: Record<string, any> = {
    payment_method: paymentMethodString,
  };

  if (paymentMethod === PaymentMethod.KNET_CARD) {
    if (token !== undefined) paymentDetails.card_token = token;
    if (cardId !== undefined) paymentDetails.card_id = cardId;
    if (cvv !== undefined) paymentDetails.cvv = cvv;
  } else {
    if (token !== undefined) paymentDetails.card_token = token;
    if (cardId !== undefined) paymentDetails.card_id = cardId;
    if (cvv !== undefined) paymentDetails.cvv = cvv;
    if (cardBin !== undefined) paymentDetails.card_bin = cardBin;
    if (cardLast4 !== undefined) paymentDetails.card_last4 = cardLast4;
    if (cardExpiryMonth !== undefined)
      paymentDetails.card_expiry_month = cardExpiryMonth;
    if (cardExpiryYear !== undefined)
      paymentDetails.card_expiry_year = cardExpiryYear;
  }

  if (fingerprint !== undefined) paymentDetails.fingerprint = fingerprint;

  return httpClient.post(`checkout/v3/${checkoutId}/select-payment-method`, {
    payment_method: paymentMethodString,
    payment_details: paymentDetails,
    ...buildPaymentRequestOptions(deviceFingerprint, options),
  });
};

const buildPaymentRequestOptions = (
  fingerprint: CosmosDeviceFingerprint,
  options?: PaymentRequestOptions,
) => ({
  terms_accepted: options?.termsAccepted ?? true,
  ...(options?.documentsSigned !== undefined
    ? { documents_signed: options.documentsSigned }
    : {}),
  ...(options?.shouldUseWalletBalance !== undefined
    ? { should_use_wallet_balance: options.shouldUseWalletBalance }
    : {}),
  ...(fingerprint.seonEnabled ? { seon_session: fingerprint.seonSession } : {}),
  ...(() => {
    const deviceId = getDeviceIdCookie();

    return deviceId ? { device_id: deviceId } : {};
  })(),
});

const postPaymentApple = (
  fingerprint: CosmosDeviceFingerprint,
  httpClient: AxiosInstance,
  checkoutId: string,
  token: string | null | undefined,
  options?: PaymentRequestOptions,
) =>
  httpClient.post(`checkout/v3/${checkoutId}/select-payment-method`, {
    payment_method: "APPLE_PAY",
    payment_details: {
      payment_method: "APPLE_PAY",
      apple_pay_token: token,
    },
    ...buildPaymentRequestOptions(fingerprint, options),
  });
